import React, { useMemo, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { today as getToday } from 'shared/src/date-string-utils/date-utils';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { PlannedValues } from './planned-values';
import {
  buildPacingBlocks,
  buildMonths,
  calculateTimeFrameDateRange,
  getDefaultSelection,
  getCurrentBlockIndex,
  getCurrentMonthIndex,
  formatDateDisplay,
  dayCount
} from './time-frame-utils';
import type { TimeFrameMode } from './time-frame-utils';

const MODE_LABELS: Record<TimeFrameMode, string> = {
  lifetime: 'Lifetime',
  pacing_blocks: 'Pacing blocks',
  months: 'Months',
  last_7_days: 'Last 7 days'
};

const MODES_WITH_SUB: Set<TimeFrameMode> = new Set(['pacing_blocks', 'months']);

type Props = {
  dateRange: ISODateRange;
  setDateRange: (range: ISODateRange) => void;
  timeFrameMode: TimeFrameMode;
  setTimeFrameMode: (mode: TimeFrameMode) => void;
};

export function TimeFrameSelector({
  dateRange,
  setDateRange,
  timeFrameMode,
  setTimeFrameMode
}: Props) {
  const { lineItem } = useLineItemContext();
  const todayISO = getToday();

  const pacingBlocks = useMemo(
    () => buildPacingBlocks(lineItem.pacing_details, lineItem.dates, todayISO),
    [lineItem.pacing_details, lineItem.dates, todayISO]
  );

  const months = useMemo(() => buildMonths(lineItem.dates, todayISO), [lineItem.dates, todayISO]);

  const defaultSelection = useMemo(
    () => getDefaultSelection(lineItem.pacing_type, pacingBlocks, months, lineItem.dates, todayISO),
    [lineItem.pacing_type, pacingBlocks, months, lineItem.dates, todayISO]
  );

  const [subIndex, setSubIndex] = useState<number | null>(defaultSelection.subIndex);

  const isDefaultView =
    timeFrameMode === defaultSelection.timeFrameMode && subIndex === defaultSelection.subIndex;

  const hasPacingBlocks = lineItem.pacing_type === 'custom' && pacingBlocks.length > 0;
  const visibleModes: TimeFrameMode[] = [
    ...(hasPacingBlocks ? ['pacing_blocks' as const] : []),
    'months',
    'last_7_days',
    'lifetime'
  ];

  const days = dayCount(dateRange);

  const handleModeChange = useCallback(
    (newMode: TimeFrameMode) => {
      let newSubIndex: number | null = null;
      if (newMode === 'pacing_blocks') {
        newSubIndex = getCurrentBlockIndex(pacingBlocks);
      } else if (newMode === 'months') {
        newSubIndex = getCurrentMonthIndex(months);
      }

      const newDateRange = calculateTimeFrameDateRange(
        newMode,
        lineItem.dates,
        pacingBlocks,
        months,
        newSubIndex,
        todayISO
      );

      setTimeFrameMode(newMode);
      setSubIndex(newSubIndex);
      setDateRange(newDateRange);
    },
    [pacingBlocks, months, lineItem.dates, todayISO, setDateRange, setTimeFrameMode]
  );

  const handleSubIndexChange = useCallback(
    (index: number) => {
      const newDateRange = calculateTimeFrameDateRange(
        timeFrameMode,
        lineItem.dates,
        pacingBlocks,
        months,
        index,
        todayISO
      );
      setSubIndex(index);
      setDateRange(newDateRange);
    },
    [timeFrameMode, lineItem.dates, pacingBlocks, months, todayISO, setDateRange]
  );

  const handleReset = useCallback(() => {
    setTimeFrameMode(defaultSelection.timeFrameMode);
    setSubIndex(defaultSelection.subIndex);
    setDateRange(defaultSelection.dateRange);
  }, [defaultSelection, setDateRange, setTimeFrameMode]);

  return (
    <div
      className="sticky top-0 z-10 bg-white pb-3 pt-6"
      style={{
        borderBottom: '1px solid',
        borderImage: 'linear-gradient(to right, #3C4F6A, #3C4F6A00) 1'
      }}>
      {/* Row 1: Primary categories + Current View + Date range */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-4">
          {visibleModes.map(mode => (
            <PrimaryModeButton
              key={mode}
              timeFrameMode={mode}
              isSelected={timeFrameMode === mode}
              hasSubSelectors={MODES_WITH_SUB.has(mode)}
              onClick={() => handleModeChange(mode)}
            />
          ))}
        </div>

        {!isDefaultView ? (
          <button
            className="rounded bg-[#3B6FEC] px-2 py-0.5 text-sm text-white cursor-pointer"
            onClick={handleReset}>
            Current view
          </button>
        ) : null}

        <div className="flex-1" />

        <div className="flex items-center gap-2 rounded border border-gray-300 px-2 py-0.5 text-sm">
          <span className="flex items-center gap-2">
            <span className="font-semibold">{formatDateDisplay(dateRange.from)}</span>
            <span className="text-gray-600">to</span>
            <span className="font-semibold">{formatDateDisplay(dateRange.to)}</span>
          </span>
          <span className="rounded bg-black/5 px-1 py-0.5 text-sm font-semibold">
            {days} {days === 1 ? 'day' : 'days'}
          </span>
        </div>
      </div>

      {/* Row 2: Sub-selectors + Planned values (always rendered for consistent height) */}
      <div className="mt-2 flex min-h-[26px] items-end justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {timeFrameMode === 'pacing_blocks'
            ? pacingBlocks.map(block => (
                <SubSelectorButton
                  key={block.index}
                  label={block.label}
                  status={block.status}
                  isSelected={subIndex === block.index}
                  onClick={() => handleSubIndexChange(block.index)}
                />
              ))
            : null}
          {timeFrameMode === 'months'
            ? months.map((month, idx) => (
                <SubSelectorButton
                  key={idx}
                  label={month.label}
                  status={month.status}
                  isSelected={subIndex === idx}
                  onClick={() => handleSubIndexChange(idx)}
                />
              ))
            : null}
        </div>
        <PlannedValues lineItemId={lineItem.id} dateRange={dateRange} category={timeFrameMode} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type PrimaryModeButtonProps = {
  timeFrameMode: TimeFrameMode;
  isSelected: boolean;
  hasSubSelectors: boolean;
  onClick: () => void;
};

function PrimaryModeButton({
  timeFrameMode,
  isSelected,
  hasSubSelectors,
  onClick
}: PrimaryModeButtonProps) {
  return (
    <button
      className={clsx('rounded px-1 py-0.5 text-sm cursor-pointer', {
        'bg-gray-800 text-white shadow': isSelected && !hasSubSelectors,
        'border border-gray-800 text-gray-800': isSelected && hasSubSelectors,
        'text-gray-600 hover:bg-gray-100': !isSelected
      })}
      onClick={onClick}>
      {MODE_LABELS[timeFrameMode]}
    </button>
  );
}

type SubSelectorButtonProps = {
  label: string;
  status: 'past' | 'current' | 'future';
  isSelected: boolean;
  onClick: () => void;
};

function SubSelectorButton({ label, status, isSelected, onClick }: SubSelectorButtonProps) {
  const isFuture = status === 'future';
  const isCurrent = status === 'current';

  return (
    <button
      disabled={isFuture}
      className={clsx('whitespace-nowrap rounded border px-1 py-0.5 text-sm', {
        'border-transparent bg-gray-800 text-white cursor-pointer': isSelected,
        'border-[#2563EB80] text-gray-600 cursor-pointer': !isSelected && isCurrent,
        'border-transparent text-gray-400 cursor-not-allowed bg-[#111928]/5': isFuture,
        'border-transparent text-gray-600 hover:bg-gray-100 cursor-pointer':
          !isSelected && !isCurrent && !isFuture
      })}
      onClick={onClick}>
      {label}
    </button>
  );
}
